import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import * as Sentry from "@sentry/react";
import { BASE_API_URL } from "./constants.js";
import {
  LOGOUT_BROADCAST_KEY,
  endSessionForLogoutBroadcast,
} from "./utils/logoutBroadcast";
import { installStaleChunkRecovery } from "./services/staleChunkReload";
import { setSessionToken } from "./services/sessionToken";
import {
  captureEntryLink,
  isClientEnvironment,
  recordsSessionReplay,
} from "./services/clientEnvironment";
import { isLocalDevelopmentOrigin } from "./utils/localOrigin";
import { loadStytchPublicTokens } from "./stytchPublicTokens";
import { isSecureLinkPath } from "./components/SecureLink/secureLinkPath";

// A client environment's entry link carries a one-time code in its fragment
// (#10718). Taken out of the address first, before error reporting below can
// record the page's address in a pageload trace, and before the local login
// shortcut rewrites the address without its fragment.
captureEntryLink();

// Local-dev login shortcut: the dev backend accepts an email address as the
// bearer token (ENV=local), but a hand-set stytch_session cookie doesn't
// survive — the Stytch SDK owns that cookie and deletes values it didn't
// mint. `?dev_session=<email>` stores the email the same way an embed
// handshake would (web storage, which the SDK never touches), so a dev
// stack can be signed into without a real Stytch flow. Vite strips the
// entire branch from production builds (import.meta.env.DEV is false).
if (import.meta.env.DEV) {
  const devParams = new URLSearchParams(window.location.search);
  const devSession = devParams.get("dev_session");
  if (devSession) {
    setSessionToken(devSession);
    devParams.delete("dev_session");
    const query = devParams.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (query ? `?${query}` : "")
    );
  }
}

// A tab open across a deploy holds chunk hashes that no longer exist (#11140).
installStaleChunkRecovery();

// Tenant deployments exclude Sentry (#11587, design §8): session replay runs
// at 100% with masking off, and a tenant must not stream its sessions to the
// shared Sentry project. Build-time exclusion is the durable fix; this gate
// keeps the VITE_ENV=tenant build from initializing meanwhile. Nor does a
// developer's machine report, on any of its loopback origins (`127.0.0.1`
// and `[::1]` as well as `localhost`), which would otherwise report as
// production (#10718).
if (
  !isLocalDevelopmentOrigin() &&
  import.meta.env.VITE_ENV !== "tenant" &&
  // A secure credential link's page exists to carry a secret, and session
  // replay records typed text, so Sentry never starts there (#12505).
  !isSecureLinkPath()
) {
  const sentryEnvironment = window.location.hostname.includes("stage")
    ? "stage"
    : "production";
  Sentry.init({
    dsn: "https://106b93f6eda965958cbc340dff84e451@o4507663621554176.ingest.us.sentry.io/4507663625814016",
    environment: sentryEnvironment,
    tunnel: `${BASE_API_URL}/userData/fptunnel`,
    integrations: [
      Sentry.browserTracingIntegration(),
      // No session replay in the client environment (#10718): it records
      // network bodies for our own origin, and there one of them carries the
      // collaborator's token.
      ...(recordsSessionReplay()
        ? [
            Sentry.replayIntegration({
              maskAllText: false,
              maskAllInputs: false,
              blockAllMedia: false,
              networkDetailAllowUrls: [
                window.location.origin,
                "finpilotai.com",
                /^https:\/\/api(\.stage)?\.finpilotai\.com/,
              ],
              // Opening a client environment answers with a one-time code
              // (#10718), and secure-link responses carry secrets (the admin
              // reveal, #12505); keep both out of recorded network bodies.
              networkDetailDenyUrls: [
                /\/api\/v2\/staff\/grants/,
                /\/secure-links/,
              ],
            }),
          ]
        : []),
      // Sentry.replayCanvasIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      // /localhost:3000/,
      // /^https:\/\/yourserver\.io\/api/,
      /finpilotai\.com/,
    ],
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
const Root = () => {
  useEffect(() => {
    const onStorage = (e) => {
      // A client tab's credential is its grant, which a home tab's logout
      // ends through the server and the token's removal (#10718).
      if (e.key === LOGOUT_BROADCAST_KEY && !isClientEnvironment()) {
        try {
          // Clear session remnants and force reload to login
          endSessionForLogoutBroadcast((url) => {
            window.location.href = url;
          });
        } catch (err) {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return <App />;
};

// A tenant build learns its Stytch project from its own backend, so the
// tokens must be in hand before any Stytch client is constructed — every
// client below is built during render (#11778). On a shared build this
// resolves immediately with the build-time tokens and nothing is fetched.
// A tenant that cannot load them renders an error rather than falling back
// to the shared test project.
const root = ReactDOM.createRoot(document.getElementById("root"));
loadStytchPublicTokens()
  .then(() => root.render(<Root />))
  .catch((err) => {
    console.error("could not load this deployment's Stytch tokens", err);
    root.render(
      <div style={{ padding: "2rem", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ fontSize: "1.1rem" }}>This deployment is not available</h1>
        <p>
          Sign-in could not be configured. Please retry shortly, and contact
          support if the problem continues.
        </p>
      </div>
    );
  });
